import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { getDispatchInfo, resolveDispatchLookupKey } from '@theme/custom-dispatch-manager';
import { ProductSelectEvent } from '@shopify/events';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * @typedef {object} StockVariant
 * @property {string} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {number} inventoryQuantity
 * @property {string|null} [estimatedRestockDate]
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {StockVariant[]} */
  #stockVariants = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#initializeOptions();
    this.#stockVariants = this.#getStockVariants();

    this.addEventListener('click', this.#handleStockTableClick);
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.updateStockStates();
    this.#updateStockTableSpecialOrderLabels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleStockTableClick);
    this.#resizeObserver.disconnect();
  }

  #initializeOptions() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      // Always push an entry so #checkedIndices stays index-aligned with the fieldsets, even when a
      // fieldset has no pre-checked option (otherwise selecting one reads undefined). Upstream Horizon PR #34.
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    // Reflect the chosen value in the option legend (e.g. the colour name) right
    // away. Without this the label only updates when the section fetch/morph below
    // resolves, which can lag several seconds on a slow connection.
    this.#updateOptionValueLabel(event.target);
    this.updateStockStates();
    const selectedVariant = this.#getSelectedStockVariant();
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
        variantId: selectedVariant?.id,
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    // Prefer CCC stock-resolved variant id when available; fall back to dataset.
    const urlVariantId = selectedVariant?.id || variantId || null;

    if (isOnProductPage) {
      if (urlVariantId) {
        url.searchParams.set('variant', urlVariantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #getStockVariants() {
    const stockData = this.querySelector('[data-variant-stock-json]');
    if (!(stockData instanceof HTMLScriptElement) || !stockData.textContent) return [];

    try {
      const parsed = JSON.parse(stockData.textContent);
      if (!Array.isArray(parsed)) return [];

      return parsed.map((variant) => ({
        id: String(variant.id),
        options: Array.isArray(variant.options) ? variant.options.map((option) => String(option)) : [],
        available: Boolean(variant.available),
        inventoryQuantity: Number(variant.inventory_quantity) || 0,
        estimatedRestockDate: variant.estimated_restock_date || null,
      }));
    } catch (error) {
      console.error('Unable to parse variant stock data:', error);
      return [];
    }
  }

  /**
   * @returns {string[]}
   */
  #getSelectedOptionValues() {
    const selectedValues = /** @type {string[]} */ ([]);
    const selectedControls = Array.from(this.querySelectorAll('fieldset input:checked, select.variant-option__select'));

    for (const control of selectedControls) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) continue;
      if (!this.#isVisibleVariantOption(control)) continue;

      if (control instanceof HTMLInputElement) {
        const optionIndex = Number(control.dataset.optionPosition) - 1;
        if (!Number.isNaN(optionIndex)) {
          selectedValues[optionIndex] = control.value;
        }
      }

      if (control instanceof HTMLSelectElement) {
        const selectedOption = control.options[control.selectedIndex];
        const optionIndex = Number(selectedOption?.dataset.optionPosition) - 1;
        if (!Number.isNaN(optionIndex)) {
          selectedValues[optionIndex] = control.value;
        }
      }
    }

    return selectedValues;
  }

  /**
   * @param {StockVariant} variant
   * @returns {'in-stock' | 'special-order' | 'sold-out'}
   */
  #getStockStatusForVariant(variant) {
    if (variant.available && variant.inventoryQuantity > 0) return 'in-stock';
    if (variant.available) return 'special-order';

    return 'sold-out';
  }

  /**
   * @param {number} optionIndex
   * @param {string} optionValue
   * @param {string[]} selectedValues
   * @returns {'in-stock' | 'special-order' | 'sold-out'}
   */
  #getStockStatusForOption(optionIndex, optionValue, selectedValues) {
    const optionCount = this.#stockVariants[0]?.options.length || 0;
    if (optionCount === 0) return 'sold-out';

    if (selectedValues[optionIndex] === optionValue) {
      const selectedVariant = this.#getSelectedStockVariant();
      if (selectedVariant) return this.#getStockStatusForVariant(selectedVariant);
    }

    let hasSpecialOrder = false;

    for (const variant of this.#stockVariants) {
      if (variant.options[optionIndex] !== optionValue) continue;

      const matchesSelectedPath = selectedValues.every((selectedValue, selectedIndex) => {
        if (!selectedValue || selectedIndex === optionIndex) return true;

        return variant.options[selectedIndex] === selectedValue;
      });

      if (!matchesSelectedPath) continue;

      const status = this.#getStockStatusForVariant(variant);
      if (status === 'in-stock') return status;
      if (status === 'special-order') hasSpecialOrder = true;
    }

    return hasSpecialOrder ? 'special-order' : 'sold-out';
  }

  /**
   * @returns {StockVariant | undefined}
   */
  #getSelectedStockVariant() {
    const selectedValues = this.#getSelectedOptionValues();
    const optionCount = this.#stockVariants[0]?.options.length || 0;

    if (optionCount === 0 || selectedValues.length < optionCount) return undefined;

    for (let index = 0; index < optionCount; index++) {
      if (!selectedValues[index]) return undefined;
    }

    return this.#stockVariants.find((variant) =>
      selectedValues.every((selectedValue, selectedIndex) => variant.options[selectedIndex] === selectedValue)
    );
  }

  #updateStockTableSpecialOrderLabels() {
    const lookupKey = resolveDispatchLookupKey(this.dataset.productType, this.dataset.productVendor);
    if (!lookupKey && !this.#stockVariants.some((variant) => variant.estimatedRestockDate)) return;

    const cells = Array.from(this.querySelectorAll('[data-variant-stock-ships-label]'));

    for (const cell of cells) {
      if (!(cell instanceof HTMLElement)) continue;

      const variantId = cell.dataset.variantId;
      const stockVariant = this.#stockVariants.find((variant) => String(variant.id) === String(variantId));
      const restockOptions = { estimatedRestockDate: stockVariant?.estimatedRestockDate || null };
      const info = getDispatchInfo(lookupKey || '', restockOptions);

      let shipsText = `Ships ${info.dateRange}`;
      if (info.mode === 'known_arrival') {
        shipsText = `Due around ${info.formattedRestockDate} - Stock in transit to us`;
      } else if (info.mode === 'expected_today') {
        shipsText = 'Stock expected today';
      } else if (info.mode === 'in_transit') {
        shipsText = 'Stock in transit';
      }

      cell.textContent = shipsText;

      const variantTitle = cell.dataset.variantTitle;
      if (variantTitle) {
        cell.setAttribute('aria-label', `${variantTitle}: ${shipsText}`);
      }
    }
  }

  updateStockStates() {
    if (this.#stockVariants.length === 0) return;

    const stockLabels = {
      'in-stock': this.dataset.stockLabelInStock || 'Ships today',
      'special-order': this.dataset.stockLabelSpecialOrder || 'Special order',
      'sold-out': this.dataset.stockLabelSoldOut || 'Sold out',
    };

    const inputs = Array.from(this.querySelectorAll('input[data-option-position]'));
    const selectedValues = this.#getSelectedOptionValues();

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const label = input.closest('[data-variant-stock-label]');
      if (!(label instanceof HTMLElement)) continue;

      const optionIndex = Number(input.dataset.optionPosition) - 1;
      if (Number.isNaN(optionIndex)) continue;

      const status = this.#getStockStatusForOption(optionIndex, input.value, selectedValues);
      const optionAvailable = status === 'sold-out' ? 'false' : 'true';
      const ariaDisabled = status === 'sold-out' ? 'true' : 'false';
      const ariaLabel = `${input.value} - ${stockLabels[status]}`;

      if (label.dataset.variantStockStatus !== status) {
        label.dataset.variantStockStatus = status;
      }

      if (input.dataset.optionAvailable !== optionAvailable) {
        input.dataset.optionAvailable = optionAvailable;
      }

      if (input.getAttribute('aria-disabled') !== ariaDisabled) {
        input.setAttribute('aria-disabled', ariaDisabled);
      }

      if (input.getAttribute('aria-label') !== ariaLabel) {
        input.setAttribute('aria-label', ariaLabel);
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleStockTableClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const cell = target.closest('[data-variant-stock-cell]');
    if (!(cell instanceof HTMLElement)) return;

    const variantId = cell.dataset.variantId;
    if (!variantId) return;

    this.#selectStockVariant(variantId);
  };

  /**
   * @param {string} variantId
   */
  #selectStockVariant(variantId) {
    const variant = this.#stockVariants.find((stockVariant) => stockVariant.id === variantId);
    if (!variant) return;

    const selectedOptions = [];

    variant.options.forEach((optionValue, index) => {
      selectedOptions.push(...this.#selectOptionValue(index + 1, optionValue));
    });

    const selectedOption =
      selectedOptions.find((option) => this.#isVisibleVariantOption(option)) || selectedOptions[selectedOptions.length - 1];

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return;

    this.updateStockStates();
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
        variantId: variant.id,
      })
    );
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption));
    this.#replaceUrlVariant(variant.id);
    this.#scrollToPurchaseArea();
  }

  /**
   * @param {number} optionPosition
   * @param {string} optionValue
   * @returns {(HTMLInputElement | HTMLOptionElement)[]}
   */
  #selectOptionValue(optionPosition, optionValue) {
    const selectedOptions = [];
    const inputs = Array.from(this.querySelectorAll(`input[data-option-position="${optionPosition}"]`));

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement) || input.value !== optionValue) continue;

      this.updateSelectedOption(input);
      this.#updateOptionValueLabel(input);
      selectedOptions.push(input);
    }

    const selects = Array.from(this.querySelectorAll('select.variant-option__select'));
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;

      const option = Array.from(select.options).find(
        (selectOption) => selectOption.dataset.optionPosition === String(optionPosition) && selectOption.value === optionValue
      );

      if (!option) continue;

      select.value = option.value;
      this.updateSelectedOption(select);
      selectedOptions.push(option);
    }

    return selectedOptions;
  }

  /**
   * @param {string} variantId
   */
  #replaceUrlVariant(variantId) {
    if (this.dataset.templateProductMatch !== 'true') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);

    if (url.href === window.location.href) return;

    yieldToMainThread().then(() => {
      history.replaceState({}, '', url.toString());
    });
  }

  #scrollToPurchaseArea() {
    const purchaseArea = this.closest('.shopify-section')?.querySelector('product-form-component') || this;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    purchaseArea.scrollIntoView({
      block: 'nearest',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Immediately reflects the newly selected value in its option legend
   * (e.g. the colour name next to "Colour"), without waiting for the section
   * fetch/morph to resolve. The subsequent morph reconciles with the server value.
   * @param {EventTarget | null} target - The changed input or select element.
   */
  #updateOptionValueLabel(target) {
    if (!(target instanceof HTMLElement)) return;

    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    let value;
    if (target instanceof HTMLInputElement) {
      value = target.value;
    } else if (target instanceof HTMLSelectElement) {
      value = target.options[target.selectedIndex]?.value;
    }
    if (value == null) return;

    const label = fieldset.querySelector('legend .variant-option__swatch-value');
    if (label instanceof HTMLElement && label.textContent !== value) {
      label.textContent = value;
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    /*
     * On the product page the response is only ever read through
     * `html.querySelector(...)` for elements that all live inside this
     * section (variant-picker, product-price, product-inventory,
     * product-form-component, media-gallery, sticky-add-to-cart). Scoping the
     * request to the host section drops the header, mega menu, footer and
     * recommendations from every variant switch — measured 1.56 MB -> 190 KB
     * on a filament PDP.
     *
     * Combined listings are the exception: those swap to a different product
     * and morph `main`, which needs the whole document.
     */
    const hostSectionId = this.#getHostSectionId();
    const loadsConnectedProduct =
      !!selectedOption.dataset.connectedProductUrl &&
      selectedOption.dataset.connectedProductUrl !== this.dataset.productUrl?.split('?')[0];

    if (hostSectionId && !loadsConnectedProduct) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${hostSectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * The Shopify section id of the section this picker renders in, but only on a
   * product page where the picker drives the whole product block.
   * @returns {string | undefined}
   */
  #getHostSectionId() {
    if (this.dataset.templateProductMatch !== 'true') return undefined;
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return undefined;

    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement) || !section.id.startsWith('shopify-section-')) return undefined;

    return section.id.slice('shopify-section-'.length);
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          const resolvedDetail = {
            html,
            productId: this.dataset.productId ?? '',
            sourceId: this.selectedOptionId,
            resource: null,
          };
          deferredEventPromise.resolve({
            variant: null,
            detail: resolvedDetail,
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            this.#dispatchLegacyVariantUpdate(variantData, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * CCC compatibility: custom scripts still listen for legacy variant:update events.
   * @param {object} variantData
   * @param {string} sourceId
   * @param {Object} data
   * @param {Document} data.html
   * @param {string} data.productId
   * @param {NewProduct} [data.newProduct]
   */
  #dispatchLegacyVariantUpdate(variantData, sourceId, data) {
    if (!sourceId || !variantData) return;

    this.dispatchEvent(
      new VariantUpdateEvent(variantData, sourceId, {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      })
    );
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#initializeOptions();
    this.#stockVariants = this.#getStockVariants();
    this.updateVariantPickerCss();
    this.updateStockStates();
    // The morph repaints special-order cells with the raw server "Special order"
    // text; re-run the enrichment pass (as connectedCallback does) so the detailed
    // dispatch ETAs are re-applied after a variant change. Without this the whole
    // stock table reverts to a generic "Special order" on every variant switch.
    this.#updateStockTableSpecialOrderLabels();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Checks if a selected option element is inside a visible variant-option wrapper.
   * Used to filter out responsive duplicate elements (desktop-only/mobile-only).
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} Whether the element's variant-option wrapper is visible.
   */
  #isVisibleVariantOption(el) {
    const wrapper = el.closest('.variant-option');
    return !wrapper || wrapper.offsetParent !== null;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const allSelected = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    const selectedOption = allSelected.find((el) => this.#isVisibleVariantOption(el));

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .filter((el) => this.#isVisibleVariantOption(el));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
